import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router";
import { useCallback } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import Layout from "../../components/Layout";
import {
  capitalize,
  DialogActions,
  DialogContent,
  Paper,
  Typography,
} from "@mui/material";
import SubmitButton from "../../components/SubmitButton";
import { useCreateBaustelleNachtrag } from "../../client/hooks";
import { useMutation, useQueryClient } from "react-query";
import { NachtragDaten } from "../../api";
import NachtragFieldsContent from "../../components/nachtraege/NachtragFieldsContent";
import { useBaustelle } from "../../client/queries";

export default function ErstelleNachtrag() {
  const { t } = useTranslation("customer");
  const { id } = useParams<{ id: string }>();
  const { mutateAsync } = useSaveNachtrag(id ?? "");
  let schema = yup.object().shape({
    bemerkung: yup.string().required(),
    datum: yup.string().required(),
    summe: yup.string().test("is-decimal", "invalid summe", (value) => {
      if (!value) return true; // Allow empty value
      const parsedValue = parseFloat(value.replace(",", "."));
      return !isNaN(parsedValue) && parsedValue >= 0;
    }),
  });
  const baustelle = useBaustelle(id!!);
  const navigate = useNavigate();
  const onSubmit = useCallback(
    async (
      values: {
        bemerkung: string;
        datum: string;
        summe: string;
      },
      formikHelpers: FormikHelpers<{
        bemerkung: string;
        datum: string;
        summe: string;
      }>
    ) => {
      let summe = values.summe
        ? parseFloat(values.summe.replace(",", "."))
        : 0.0;
      await mutateAsync({
        bemerkung: values.bemerkung,
        datum: values.datum + "T00:00:00.720Z",
        summe: summe,
      });
      navigate("/baustellen/" + id + "/stammdaten");
      const { setSubmitting, setStatus } = formikHelpers;

      setStatus(undefined);
      try {
      } catch (error: any) {
        setStatus(error.message);
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, mutateAsync]
  );
  return (
    <Layout title={"Nachtrag erstellen"} onBackClick={() => navigate(-1)}>
      <Paper
        elevation={1}
        sx={{ marginTop: 2, padding: 3, marginLeft: 2, marginRight: 2 }}
      >
        <Typography>{"Baustelle " + baustelle.name}</Typography>
      </Paper>
      <Paper
        elevation={1}
        sx={{ marginTop: 2, padding: 3, marginLeft: 2, marginRight: 2 }}
      >
        <Formik
          initialValues={{
            bemerkung: "",
            datum: "",
            summe: "",
          }}
          validationSchema={schema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, isValidating, isValid, dirty }) => (
            <>
              <Typography variant="h5">{"Nachtrag"}</Typography>
              <DialogContent>
                <Form id="new-nachtrag">
                  <NachtragFieldsContent />
                </Form>
              </DialogContent>
              <DialogActions>
                <SubmitButton
                  form="new-nachtrag"
                  type="submit"
                  variant="contained"
                  color="secondary"
                  loading={isSubmitting}
                  disabled={isValidating || !isValid || !dirty}
                >
                  {capitalize(t("save"))}
                </SubmitButton>
              </DialogActions>
            </>
          )}
        </Formik>
      </Paper>
    </Layout>
  );
}

function useSaveNachtrag(id: string) {
  const createNachtrag = useCreateBaustelleNachtrag();
  const queryClient = useQueryClient();

  return useMutation(
    (input: NachtragDaten) => {
      return createNachtrag(id, input);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(["nachtraege"]);
      },
    }
  );
}
